import React, { useCallback, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Pagination,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import GateTriggerControls from './GateTriggerControls.jsx';

const t = (text) => {
    if (typeof window !== 'undefined' && typeof window.__ === 'function') {
        try {
            return window.__(text);
        } catch (error) {
            return text;
        }
    }
    return text;
};

const emptyGate = {
    id: null,
    name: '',
    type: 'barrier',
    description: '',
    deviceId: null,
    access_list_ids: [],
};

const resolveEndpoint = (template, id) => (template || '').replace('__GATE_ID__', String(id));

const normalizeRelayConfigs = (value) => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((entry) => {
            if (!entry || typeof entry !== 'object') {
                return null;
            }

            const channel = Number(entry.index ?? entry.channel ?? entry.id);
            const timeout = Number(entry.timeout ?? 2);
            if (!Number.isFinite(channel) || channel <= 0) {
                return null;
            }

            return {
                tip: ['out', 'in'].includes(String(entry.tip ?? entry.type ?? 'out')) ? String(entry.tip ?? entry.type ?? 'out') : 'out',
                action: ['open', 'close'].includes(String(entry.action ?? 'open')) ? String(entry.action ?? 'open') : 'open',
                index: channel,
                timeout: Number.isFinite(timeout) && timeout >= 0 ? Number(timeout.toFixed(2)) : 2,
            };
        })
        .filter(Boolean)
        .sort((left, right) => left.index - right.index);
};

/**
 * Manage GPIO gates definitions while device mapping lives on the device pages.
 */
export default function GateManagementApp({ gates = [], accessLists = [], endpoints = {}, csrfToken = '' }) {
    const [rows, setRows] = useState(Array.isArray(gates) ? gates : []);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [listsDialogOpen, setListsDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [listSearch, setListSearch] = useState('');
    const [listPage, setListPage] = useState(1);
    const [activeListId, setActiveListId] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [form, setForm] = useState(emptyGate);

    const selectedAccessLists = useMemo(
        () => accessLists.filter((entry) => form.access_list_ids.includes(entry.id)),
        [accessLists, form.access_list_ids],
    );
    const selectedPersonDeviceId = useMemo(() => {
        if (form.type !== 'door') {
            return null;
        }

        if (form.deviceId) {
            return Number(form.deviceId);
        }

        const firstSelectedPersonList = selectedAccessLists.find((entry) => Number(entry.device_id || 0) > 0);
        return firstSelectedPersonList ? Number(firstSelectedPersonList.device_id || 0) : null;
    }, [form.deviceId, form.type, selectedAccessLists]);

    const accessListOptions = useMemo(
        () => accessLists.filter((entry) => {
            const expectedType = form.type === 'door' ? 'person' : 'license_plate';
            if (entry.type !== expectedType) {
                return false;
            }

            if (expectedType === 'person') {
                if (!selectedPersonDeviceId) {
                    return true;
                }

                return Number(entry.device_id || 0) === selectedPersonDeviceId;
            }

            return true;
        }),
        [accessLists, form.type, selectedPersonDeviceId],
    );
    const activeList = useMemo(() => {
        if (!activeListId && selectedAccessLists.length > 0) {
            return selectedAccessLists[0];
        }
        return selectedAccessLists.find((entry) => entry.id === activeListId) || selectedAccessLists[0] || null;
    }, [activeListId, selectedAccessLists]);
    const filteredEntries = useMemo(() => {
        if (selectedAccessLists.length === 0) {
            return [];
        }

        const query = listSearch.trim().toLowerCase();
        const scopedLists = activeList ? selectedAccessLists.filter((list) => list.id === activeList.id) : selectedAccessLists;

        return scopedLists
            .flatMap((list) => {
                const entries = Array.isArray(list.entries) ? list.entries : [];
                return entries.map((entry, index) => ({
                    ...entry,
                    source_list_id: list.id,
                    source_list_name: list.name,
                    _key: `${list.id}-${entry.id ?? entry.label}-${index}`,
                }));
            })
            .filter((entry) => {
                if (query === '') {
                    return true;
                }
                const haystack = `${entry.label || ''} ${entry.meta || ''} ${entry.source_list_name || ''}`.toLowerCase();
                return haystack.includes(query);
            });
    }, [activeList, listSearch, selectedAccessLists]);
    const pageSize = 24;
    const paginatedEntries = useMemo(() => {
        const start = (listPage - 1) * pageSize;
        return filteredEntries.slice(start, start + pageSize);
    }, [filteredEntries, listPage]);
    const listPageCount = Math.max(1, Math.ceil(filteredEntries.length / pageSize));

    const apiFetch = useCallback(async (url, options = {}) => {
        const response = await fetch(url, {
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
                ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
                ...options.headers,
            },
            ...options,
        });

        if (!response.ok) {
            let payload = {};
            try {
                payload = await response.json();
            } catch (error) {
                payload = {};
            }

            const error = new Error(payload.message || 'Request failed');
            error.payload = payload;
            throw error;
        }

        return response.json();
    }, [csrfToken]);

    const openCreateDialog = useCallback(() => {
        setForm(emptyGate);
        setFormErrors({});
        setDialogOpen(true);
    }, []);

    const openEditDialog = useCallback((row) => {
        setForm({
            id: row.id,
            name: row.name || '',
            type: row.type || 'barrier',
            description: row.description || '',
            deviceId: row.device_id || null,
            access_list_ids: Array.isArray(row.access_list_ids) ? row.access_list_ids : [],
            device_edit_url: row.device_edit_url || null,
        });
        setFormErrors({});
        setListSearch('');
        setListPage(1);
        setActiveListId((Array.isArray(row.access_list_ids) && row.access_list_ids.length > 0) ? row.access_list_ids[0] : null);
        setDialogOpen(true);
    }, []);

    const saveGate = useCallback(async () => {
        setSaving(true);
        setFormErrors({});
        try {
            const payload = {
                name: form.name,
                type: form.type,
                description: form.description,
                access_list_ids: form.access_list_ids,
            };
            const url = form.id ? resolveEndpoint(endpoints.update, form.id) : endpoints.create;
            const method = form.id ? 'PUT' : 'POST';
            const result = await apiFetch(url, { method, body: JSON.stringify(payload) });
            const saved = result.data;
            setRows((current) => {
                const next = form.id ? current.map((row) => (row.id === saved.id ? saved : row)) : [...current, saved];
                return next.slice().sort((left, right) => left.name.localeCompare(right.name));
            });
            setDialogOpen(false);
            setFeedback({ severity: 'success', message: form.id ? t('Gate updated.') : t('Gate created.') });
        } catch (error) {
            setFormErrors(error.payload?.errors || {});
            setFeedback({ severity: 'error', message: error.message || t('Failed to save gate.') });
        } finally {
            setSaving(false);
        }
    }, [apiFetch, endpoints.create, endpoints.update, form]);

    const deleteGate = useCallback(async (row) => {
        if (!window.confirm(t('Delete this gate?'))) {
            return;
        }

        try {
            await apiFetch(resolveEndpoint(endpoints.delete, row.id), { method: 'DELETE' });
            setRows((current) => current.filter((entry) => entry.id !== row.id));
            setFeedback({ severity: 'success', message: t('Gate deleted.') });
        } catch (error) {
            setFeedback({ severity: 'error', message: error.message || t('Failed to delete gate.') });
        }
    }, [apiFetch, endpoints.delete]);

    const openListsDialog = useCallback(() => {
        setListSearch('');
        setListPage(1);
        setActiveListId(selectedAccessLists[0]?.id ?? null);
        setListsDialogOpen(true);
    }, [selectedAccessLists]);

    return (
        <Stack spacing={3}>
            {feedback && <Alert severity={feedback.severity}>{feedback.message}</Alert>}

            <Card variant="outlined">
                <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                        <Box>
                            <Typography variant="h6">{t('Assigned barriers and doors')}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('Define gates and their access lists here. Device, relay count, and relay assignment are configured from Device Management.')}
                            </Typography>
                        </Box>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>{t('Add gate')}</Button>
                    </Stack>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('Name')}</TableCell>
                                <TableCell>{t('Type')}</TableCell>
                                <TableCell>{t('Device')}</TableCell>
                                <TableCell>{t('Relays')}</TableCell>
                                <TableCell>{t('Access lists')}</TableCell>
                                <TableCell>{t('Relay Details')}</TableCell>
                                <TableCell align="right">{t('Actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography variant="body2" color="text.secondary">{t('No gates defined yet.')}</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((row) => {
                                const assignedLists = accessLists.filter((list) => (row.access_list_ids || []).includes(list.id));
                                const relayButtons = normalizeRelayConfigs(row.relays).map((entry) => ({
                                    index: entry.index,
                                    tip: entry.tip,
                                    action: entry.action,
                                    timeout: entry.timeout,
                                    label: row.relay_labels?.[entry.index]?.label || `Relay ${entry.index}`,
                                }));

                                return (
                                    <TableRow key={row.id} hover>
                                        <TableCell>
                                            <Typography variant="subtitle2">{row.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{row.description || ' - '}</Typography>
                                        </TableCell>
                                        <TableCell>{row.type === 'door' ? t('Door') : t('Barrier')}</TableCell>
                                        <TableCell>{row.device_name || ' - '}</TableCell>
                                        <TableCell>
                                            <GateTriggerControls
                                                gates={[row]}
                                                triggerUrlTemplate={endpoints.trigger}
                                                csrfToken={csrfToken}
                                                showGateName={false}
                                                emptyText={t('Assigned from device page')}
                                                onSuccess={(message) => setFeedback({ severity: 'success', message: message || t('Command sent successfully.') })}
                                                onError={(message) => setFeedback({ severity: 'error', message: message || t('Failed to trigger relay.') })}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                {assignedLists.length ? assignedLists.map((list) => <Chip key={`${row.id}-${list.id}`} size="small" label={list.name} />) : <Typography variant="caption" color="text.secondary">{t('None')}</Typography>}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                {relayButtons.length
                                                    ? relayButtons.map((relay) => <Chip key={`timeout-${row.id}-${relay.index}`} size="small" label={`${relay.label} | ${relay.tip} | ${relay.action} | ${relay.timeout}s`} />)
                                                    : <Typography variant="caption" color="text.secondary">{t('Map from Device Management')}</Typography>}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button size="small" startIcon={<EditIcon />} onClick={() => openEditDialog(row)}>{t('Edit')}</Button>
                                            <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => deleteGate(row)}>{t('Delete')}</Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{form.id ? t('Edit gate') : t('Add gate')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label={t('Name')} value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} error={Boolean(formErrors.name)} helperText={formErrors.name?.[0]} />
                        <TextField
                            select
                            label={t('Type')}
                            value={form.type}
                            onChange={(event) => setForm((current) => ({ ...current, type: event.target.value, access_list_ids: [] }))}
                            SelectProps={{ native: true }}
                        >
                            <option value="barrier">{t('Barrier')}</option>
                            <option value="door">{t('Door')}</option>
                        </TextField>
                        <TextField label={t('Description')} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} multiline minRows={2} />
                        <Typography variant="caption" color="text.secondary">
                            {t('Device assignment and relay mapping are configured from Device Management after the gate is created.')}
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            {form.device_edit_url && (
                                <Button
                                    component="a"
                                    href={form.device_edit_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    variant="outlined"
                                >
                                    {t('Open device in new tab')}
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                onClick={openListsDialog}
                                disabled={selectedAccessLists.length === 0}
                            >
                                {t('View list contents')}
                            </Button>
                        </Stack>
                        <Autocomplete
                            multiple
                            options={accessListOptions}
                            value={selectedAccessLists}
                            getOptionLabel={(option) => option.name || `${option.id}`}
                            onChange={(event, value) => setForm((current) => {
                                if (current.type !== 'door') {
                                    return { ...current, access_list_ids: value.map((entry) => entry.id) };
                                }

                                const anchorDeviceId = current.deviceId
                                    ? Number(current.deviceId)
                                    : Number(value.find((entry) => Number(entry.device_id || 0) > 0)?.device_id || 0);
                                const nextValue = !anchorDeviceId
                                    ? value
                                    : value.filter((entry) => Number(entry.device_id || 0) === anchorDeviceId);

                                return { ...current, access_list_ids: nextValue.map((entry) => entry.id) };
                            })}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={form.type === 'door' ? t('Person access lists') : t('License plate access lists')}
                                    helperText={form.type === 'door' && !form.deviceId
                                        ? (selectedPersonDeviceId
                                            ? t('After the first selected list, only person lists from that same device remain available.')
                                            : t('All person lists are visible until you choose the first one or assign this gate from Device Management.'))
                                        : t('Attach one or more reusable authorization lists.')}
                                />
                            )}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>{t('Cancel')}</Button>
                    <Button variant="contained" onClick={saveGate} disabled={saving}>{form.id ? t('Save changes') : t('Create gate')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={listsDialogOpen} onClose={() => setListsDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{t('List contents')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {selectedAccessLists.map((list) => (
                                <Button
                                    key={`list-select-${list.id}`}
                                    variant={activeList?.id === list.id ? 'contained' : 'outlined'}
                                    onClick={() => {
                                        setActiveListId(list.id);
                                        setListPage(1);
                                    }}
                                >
                                    {list.name}
                                </Button>
                            ))}
                        </Stack>
                        <TextField
                            label={t('Search in list')}
                            value={listSearch}
                            onChange={(event) => {
                                setListSearch(event.target.value);
                                setListPage(1);
                            }}
                            helperText={t('Search by number plate or person name / id.')}
                        />
                        {activeList ? (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    {activeList
                                        ? `${activeList.name} · ${filteredEntries.length} ${t('entries')}`
                                        : `${filteredEntries.length} ${t('entries')}`}
                                </Typography>
                                <Box sx={{ columnCount: { xs: 1, sm: 2, md: 3 }, columnGap: 2 }}>
                                    {paginatedEntries.map((entry, index) => (
                                        <Box
                                            key={entry._key || `${activeList.id}-${entry.id ?? entry.label}-${index}`}
                                            sx={{
                                                breakInside: 'avoid',
                                                border: '1px solid rgba(0,0,0,0.12)',
                                                borderRadius: 1,
                                                px: 1.5,
                                                py: 1,
                                                mb: 1,
                                                backgroundColor: '#fff',
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.label}</Typography>
                                            {entry.meta && <Typography variant="caption" color="text.secondary">{entry.meta}</Typography>}
                                            {entry.source_list_name && selectedAccessLists.length > 1 && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{entry.source_list_name}</Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                                <Stack alignItems="center">
                                    <Pagination
                                        count={listPageCount}
                                        page={Math.min(listPage, listPageCount)}
                                        onChange={(event, page) => setListPage(page)}
                                        color="primary"
                                    />
                                </Stack>
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">{t('No access list selected.')}</Typography>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setListsDialogOpen(false)}>{t('Close')}</Button>
                </DialogActions>
            </Dialog>

        </Stack>
    );
}